import * as React from 'react';
import { createContext, useContext, useMemo } from 'react';

import { useLayer } from '@coursera-web/statsig';

import { CoachStateProvider } from 'bundles/unified-common/providers/CoachStateProvider';
import { useIsCourseDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';
import { useIsS12nDataLoading } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import { useTwoPanelConversationalDiscoveryExperiment } from 'bundles/unified-description-page-common/providers/specialization/TwoPanelConversationalDiscoveryProvider';

const LAYER_NAME = 'conversational-discovery-xdp-mobile-web-layer';
const PARAM_NAME = 'chat_tray';
const MINIMAL_TRAY = 'minimal';
const CONTEXTUAL_TRAY = 'contextual';
const NO_TRAY = 'none';

export type MobileWebConversationalDiscoveryExperimentContextType = {
  mobileWebConversationalDiscoveryEnabled: boolean;
  minimalTray: boolean;
  contextualTray: boolean;
};

export const MobileWebConversationalDiscoveryExperimentContext =
  createContext<MobileWebConversationalDiscoveryExperimentContextType>({
    mobileWebConversationalDiscoveryEnabled: false,
    minimalTray: false,
    contextualTray: false,
  });

function MobileWebConversationalDiscoveryExperimentProvider({ children }: React.PropsWithChildren) {
  const isS12nDataLoading = useIsS12nDataLoading();
  const isCourseDataLoading = useIsCourseDataLoading();

  // Read unconditionally, before any gate: moving layer.get() behind a condition changes the
  // exposed population and is what caused the two-panel SRM incident.
  const layer = useLayer(LAYER_NAME);
  const tray = layer.get(PARAM_NAME, NO_TRAY);

  const { twoPanelConversationalDiscoveryEnabled } = useTwoPanelConversationalDiscoveryExperiment();

  const value = useMemo(() => {
    // The tray is the mobile-UA surface and two-panel is the desktop-UA one; Statsig's targeting
    // already keeps them apart. Standing down here keeps that exclusivity true in code: two-panel
    // seeds the shared coachOpen above us, so a tray CoachStateProvider would shadow it for the
    // whole page — the docked chat would render permanently empty.
    // ponytail: if the tray ever needs to run on a desktop UA, this exclusivity is the wrong model
    // — hoist a single CoachStateProvider into the page-data providers instead (see MERCH-3455).
    const trayEligible = !twoPanelConversationalDiscoveryEnabled;
    const minimalTray = trayEligible && tray === MINIMAL_TRAY;
    const contextualTray = trayEligible && tray === CONTEXTUAL_TRAY;
    return {
      mobileWebConversationalDiscoveryEnabled: minimalTray || contextualTray,
      minimalTray,
      contextualTray,
    };
  }, [tray, twoPanelConversationalDiscoveryEnabled]);

  return (
    <MobileWebConversationalDiscoveryExperimentContext.Provider value={value}>
      {value.mobileWebConversationalDiscoveryEnabled ? (
        // Enabled implies a mobile UA, which is always below the desktop breakpoint, so the tray
        // opens on load with no viewport sync to flip it.
        <CoachStateProvider
          coachOpen
          isFullScreen={false}
          isContextConfigLoading={isS12nDataLoading || isCourseDataLoading}
        >
          {children}
        </CoachStateProvider>
      ) : (
        children
      )}
    </MobileWebConversationalDiscoveryExperimentContext.Provider>
  );
}

export const useMobileWebConversationalDiscoveryExperiment = () => {
  return useContext(MobileWebConversationalDiscoveryExperimentContext);
};

export default MobileWebConversationalDiscoveryExperimentProvider;
